import React from "react";
import { Button, ButtonProps } from "@mui/material";
import { EventFormState, EventTypeOption } from "../../eventMockData";
// cpaGold/cpaGoldDark come from the shared dashboard formStyles (both it and
// eventSalesShared alias commonStyles, so this re-point is zero-risk — B6/F).
import { cpaGold, cpaGoldDark } from "../../../../common/dashboard/formStyles";
import { commonStyles } from "../../../../common/commonStyles";

export type WizardAction = "draft" | "publish";

export type EventTypeLibraryHandlers = {
  onAddEventType?: (label: string, color: string) => Promise<EventTypeOption | null>;
  onArchiveEventType?: (eventTypeId: string) => Promise<void>;
};

export type WizardStepProps = EventTypeLibraryHandlers & {
  event: EventFormState;
  setEvent: (updater: (prev: EventFormState) => EventFormState) => void;
  eventTypes: EventTypeOption[];
  chamberId: string;
  errors: Record<string, string>;
};

// Wizard row ids are persisted with the event and reloaded in later sessions.
// A bare counter restarts at 0 on every page load, so a freshly added row
// (e.g. "speaker-1") collides with a persisted row of the same id and edits
// override each other (EVT-CREATE-006/007). The per-load random session
// segment keeps ids unique across page loads.
const wizardIdSession = Math.random().toString(36).slice(2, 10);
let wizardIdCounter = 0;
export const wizardId = (prefix: string): string => {
  wizardIdCounter += 1;
  return `${prefix}-${wizardIdSession}-${wizardIdCounter}`;
};

// Shared drag-handle reorder for the wizard tables (speakers, agenda, ticket
// types, sponsor levels, add-ons): every mock row shows a six-dot drag handle,
// so the handle must actually reorder rows, not just decorate them.
export const moveWizardRow = <T,>(
  rows: T[],
  fromIndex: number,
  toIndex: number
): T[] => {
  const next = [...rows];
  const [moved] = next.splice(fromIndex, 1);
  next.splice(toIndex, 0, moved);
  return next;
};

export const normalizeWizardTimeInput = (value: string): string => {
  const trimmedValue = value.trim();
  if (!trimmedValue) return "";

  const twentyFourHourMatch = trimmedValue.match(/^([01]?\d|2[0-3]):([0-5]\d)$/);
  if (twentyFourHourMatch) {
    return `${twentyFourHourMatch[1].padStart(2, "0")}:${twentyFourHourMatch[2]}`;
  }

  const twelveHourMatch = trimmedValue.match(/^(\d{1,2})(?::([0-5]\d))?\s*([ap])\.?m\.?$/i);
  if (!twelveHourMatch) return trimmedValue;

  const rawHours = Number(twelveHourMatch[1]);
  if (rawHours < 1 || rawHours > 12) return trimmedValue;

  const minutes = twelveHourMatch[2] || "00";
  const meridiem = twelveHourMatch[3].toLowerCase();
  const hours =
    meridiem === "p"
      ? rawHours === 12
        ? 12
        : rawHours + 12
      : rawHours === 12
        ? 0
        : rawHours;

  return `${String(hours).padStart(2, "0")}:${minutes}`;
};

export const normalizeEventDateTimeFields = (
  event: EventFormState
): EventFormState => ({
  ...event,
  endDate: event.endDate || event.startDate,
  startTime: normalizeWizardTimeInput(event.startTime),
  endTime: normalizeWizardTimeInput(event.endTime),
  // Fully-empty agenda rows are add-row scaffolding only; drop them so they
  // never reach validation or the backend (EVT-PUBLISH-001).
  agendaItems: (event.agendaItems || []).filter(
    (agendaItem) => agendaItem.timeLabel.trim() || agendaItem.details.trim()
  ),
});

// WizardSection was deleted in the B6 polish pass: all five call sites
// (Step2 x2, Step3 x2, Step4 x1) now use the shared
// common/dashboard/SectionShell (variant="pill", Q-037 normalization).

// Event-wizard checkboxes are green (July 9, 2026 direction) — this overrides
// the gold Q-008 platform default within the create/update event wizard only.
export const wizardGreenCheckboxSx = {
  "&.Mui-checked": { color: commonStyles.cmpGreen },
};

export const FieldLabel = ({
  children,
  required,
  hint,
}: {
  children: React.ReactNode;
  required?: boolean;
  hint?: string;
}): React.ReactElement => (
  <div className="mb-3">
    <span className="text-[16px] font-extrabold leading-6 text-gray-950">
      {children}
      {required ? <span className="text-red-500"> *</span> : null}
    </span>
    {hint ? <p className="mt-2 text-[15px] leading-6 text-gray-500">{hint}</p> : null}
  </div>
);

type WizardActionTone = "black" | "gold" | "red" | "green" | "outline";

const wizardActionToneSx: Record<WizardActionTone, ButtonProps["sx"]> = {
  black: {
    backgroundColor: "#000",
    color: "#fff",
    "&:hover": { backgroundColor: "#111827" },
  },
  gold: {
    backgroundColor: cpaGold,
    color: "#fff",
    "&:hover": { backgroundColor: cpaGoldDark },
  },
  red: {
    backgroundColor: "#ff1728",
    color: "#fff",
    "&:hover": { backgroundColor: "#d90f1e" },
  },
  green: {
    // #12bd7b is the mock-verified "Schedule to Publish" green (Q-006 /
    // screenshot parity) — do NOT normalize it to cmpGreen #10B981.
    backgroundColor: "#12bd7b",
    color: "#fff",
    "&:hover": { backgroundColor: "#0ea66b" },
  },
  outline: {
    borderColor: "#d1d5db",
    color: "#1f2937",
    "&:hover": {
      borderColor: "#9ca3af",
      backgroundColor: "#f9fafb",
    },
  },
};

export const WizardActionButton = ({
  tone = "gold",
  sx,
  children,
  ...props
}: ButtonProps & {
  tone?: WizardActionTone;
  // Allow rendering as a router link (e.g. the wizard Cancel button) so the
  // shared unsaved-changes anchor interception still applies.
  component?: React.ElementType;
  to?: string;
}): React.ReactElement => (
  <Button
    {...props}
    variant={tone === "outline" ? "outlined" : "contained"}
    sx={[
      {
        borderRadius: "6px",
        boxShadow: "none",
        fontSize: props.size === "small" ? 12 : 16,
        fontWeight: 800,
        lineHeight: 1.2,
        minHeight: props.size === "small" ? 27 : 52,
        px: props.size === "small" ? 1.4 : 2.8,
        textTransform: "none",
        whiteSpace: "nowrap",
        "&:hover": { boxShadow: "none" },
        "& .MuiButton-startIcon": {
          mr: 0.75,
          "& svg": { fontSize: props.size === "small" ? 15 : 22 },
        },
        "& .MuiButton-endIcon": {
          ml: 1,
          "& svg": { fontSize: props.size === "small" ? 15 : 24 },
        },
      },
      wizardActionToneSx[tone],
      ...(Array.isArray(sx) ? sx : sx ? [sx] : []),
    ]}
  >
    {children}
  </Button>
);

export const wizardTextFieldSx = {
  "& .MuiInputBase-root": {
    borderRadius: "6px",
    fontSize: 16,
    minHeight: 58,
  },
  "& .MuiInputBase-input": {
    paddingBottom: "14px",
    paddingTop: "14px",
  },
};

// Wizard-skin overrides for the shared CurrencyInput (POL-005): reproduce the
// 58px / 16px MUI-outline look of wizardTextFieldSx so the popup price fields
// do not shrink to CurrencyInput's h-11 dashboard default. rounded-md = the
// 6px radius wizardTextFieldSx uses.
export const wizardCurrencyInputClassName =
  "h-[58px] w-full rounded-md border border-gray-300 bg-white pl-8 pr-4 text-[16px] text-gray-900 outline-none transition placeholder:text-gray-400 focus:border-cpaGold focus:ring-2 focus:ring-amber-100";
export const wizardCurrencyAdornmentClassName =
  "pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-[16px] text-gray-500";
